import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Power,
  FileText,
  TrendingUp,
  ChevronRight,
  Truck,
  Tag,
  Calendar,
  Receipt,
  Wallet,
  Filter,
} from "lucide-react";
import SearchableSelect from "../components/SearchableSelect";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";

export default function UnitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Mock Data (tidak diubah, mempertahankan integrasi yang ada)
  const unitData = {
    name: "KOMATSU 08",
    type: "Alat Berat",
    totalTx: 108,
    totalUsage: 12185000,
    usagePercentage: 24,
    lastTx: "24 Jan 2026, 11:46",
    status: "Aktif",
  };

  const transactionData = [
    {
      id: 1,
      date: "04 Jan 2026, 09:30",
      sparepart: "Kuku Macan Komatsu",
      quantity: "5 unit",
      unitPrice: 350000,
      total: 1750000,
      receiver: "Rendi",
    },
    {
      id: 2,
      date: "04 Jan 2026, 09:30",
      sparepart: "Baut Kuku Komatsu",
      quantity: "5 unit",
      unitPrice: 45000,
      total: 225000,
      receiver: "Rendi",
    },
    {
      id: 3,
      date: "18 Jan 2026, 09:30",
      sparepart: "Oli 90 Premium",
      quantity: "20 liter",
      unitPrice: 35000,
      total: 700000,
      receiver: "Imam",
    },
    {
      id: 4,
      date: "18 Jan 2026, 08:30",
      sparepart: "Fuel Filter 5611",
      quantity: "1 set",
      unitPrice: 440000,
      total: 440000,
      receiver: "Imam",
    },
    {
      id: 5,
      date: "24 Jan 2026, 11:46",
      sparepart: "Vanbelt 3289157",
      quantity: "1 pcs",
      unitPrice: 378000,
      total: 378000,
      receiver: "Helper",
    },
  ];

  const formatCurrency = (val) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(val || 0);

  const sidebarToggle = () =>
    setIsSidebarCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
      return newState;
    });

  const isActive = unitData.status === "Aktif";

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={sidebarToggle}
        activePage="inventori"
      />

      <div
        className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />

        <div className="p-6 lg:p-8 max-w-full">
          {/* === Breadcrumb === */}
          <nav className="flex items-center text-xs text-slate-500 mb-3">
            <button
              onClick={() => navigate("/inventori")}
              className="hover:text-orange-600 transition-colors">
              Inventori
            </button>
            <ChevronRight className="w-3 h-3 mx-1.5 text-slate-400" />
            <button
              onClick={() => navigate("/inventori")}
              className="hover:text-orange-600 transition-colors">
              Unit
            </button>
            <ChevronRight className="w-3 h-3 mx-1.5 text-slate-400" />
            <span className="text-slate-700 font-medium truncate max-w-[280px]">
              {unitData.name}
            </span>
          </nav>

          {/* === Back Button (di luar card) === */}
          <button
            onClick={() => navigate("/inventori")}
            className="inline-flex items-center gap-2 mb-4 px-3 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-slate-600 hover:text-orange-700 hover:ring-orange-300 hover:bg-orange-50 transition-all text-sm font-medium shadow-sm">
            <ArrowLeft className="w-4 h-4" />
            <span>Kembali ke halaman Inventori</span>
          </button>

          {/* === Hero Header === */}
          <div className="relative bg-gradient-to-br from-amber-500 via-orange-600 to-rose-600 rounded-3xl p-6 lg:p-8 mb-6 overflow-hidden shadow-lg shadow-amber-900/10">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-10 w-60 h-60 bg-yellow-300/10 rounded-full blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-5">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/20 flex items-center justify-center flex-shrink-0">
                  <Truck className="w-8 h-8 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-100/90 mb-0.5">
                    Riwayat Transaksi Unit
                  </p>
                  <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight leading-tight truncate">
                    {unitData.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="inline-flex items-center gap-1 text-xs text-amber-50 bg-white/10 px-2.5 py-1 rounded-full ring-1 ring-white/20">
                      <Tag className="w-3 h-3" /> {unitData.type}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${
                        isActive
                          ? "bg-emerald-400/20 text-emerald-50 ring-emerald-300/40"
                          : "bg-rose-400/20 text-rose-50 ring-rose-300/40"
                      }`}>
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-300" : "bg-rose-300"}`}
                      />
                      {unitData.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  className="inline-flex items-center gap-2 px-3 py-2.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white rounded-xl ring-1 ring-white/20 transition-colors text-sm font-medium"
                  title="Ubah Status">
                  <Power className="w-4 h-4" />
                  <span className="hidden sm:inline">Status</span>
                </button>
                <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-orange-700 rounded-xl hover:bg-orange-50 font-semibold text-sm shadow-md shadow-amber-900/20 transition-colors">
                  <FileText className="w-4 h-4" />
                  Cetak Laporan
                </button>
              </div>
            </div>
          </div>

          {/* === Stat Cards === */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard
              icon={<Receipt className="w-5 h-5" />}
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
              label="Transaksi Bulan Ini"
              value={
                <>
                  {unitData.totalTx}{" "}
                  <span className="text-sm font-normal text-slate-400">
                    Nota
                  </span>
                </>
              }
              hint="Total nota transaksi"
            />
            <StatCard
              icon={<Wallet className="w-5 h-5" />}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              label="Pemakaian Bulan Ini"
              value={formatCurrency(unitData.totalUsage)}
              hint={
                <span className="inline-flex items-center gap-1 text-rose-600 font-semibold">
                  <TrendingUp className="w-3 h-3" /> +{unitData.usagePercentage}
                  % vs bulan lalu
                </span>
              }
              accent
            />
            <StatCard
              icon={<Calendar className="w-5 h-5" />}
              iconBg="bg-violet-50"
              iconColor="text-violet-600"
              label="Transaksi Terakhir"
              value={<span className="text-base">{unitData.lastTx}</span>}
              hint="Aktivitas terakhir unit"
            />
            <StatCard
              icon={<Tag className="w-5 h-5" />}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              label="Jenis Unit"
              value={<span className="text-base">{unitData.type}</span>}
              hint="Kategori unit"
            />
          </div>

          {/* === Transaction Table === */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
                  <Receipt className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900">
                    Riwayat Transaksi
                  </h2>
                  <p className="text-xs text-slate-500">
                    {transactionData.length} transaksi terakhir
                  </p>
                </div>
              </div>
              <button className="inline-flex items-center gap-2 px-3 py-2 bg-white ring-1 ring-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 transition-colors text-sm">
                <Filter className="w-4 h-4" />
                <span>Filter</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50/80 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-16">
                      No
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Tanggal Transaksi
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Nama Sparepart
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Jumlah
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Harga Satuan
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Total Pemakaian
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Penerima
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {transactionData.map((item, index) => (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {item.date}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {item.sparepart}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {item.quantity}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {formatCurrency(item.unitPrice)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900 font-semibold">
                        {formatCurrency(item.total)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                          <span className="w-5 h-5 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-white text-[10px] flex items-center justify-center font-bold">
                            {item.receiver?.charAt(0)?.toUpperCase()}
                          </span>
                          {item.receiver}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">
                  Menampilkan 1 - 10 dari 99 entri
                </span>
                <SearchableSelect
                  options={[
                    { value: 10, label: "10" },
                    { value: 25, label: "25" },
                    { value: 50, label: "50" },
                    { value: 100, label: "100" },
                  ]}
                  value={10}
                  onChange={() => {}}
                  placeholder="Per halaman"
                  displayKey="label"
                  valueKey="value"
                  className="w-20"
                  searchable={false}
                  size="sm"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-50 transition">
                  ‹‹
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-50 transition">
                  ‹
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-600 text-white text-sm font-semibold shadow-sm shadow-teal-600/20 transition">
                  1
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium transition">
                  2
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium transition">
                  3
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-50 transition">
                  ›
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-50 transition">
                  ››
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  icon,
  iconBg,
  iconColor,
  label,
  value,
  hint,
  accent = false,
}) {
  return (
    <div
      className={`relative bg-white p-5 rounded-2xl ring-1 ring-slate-200/70 shadow-sm hover:shadow-md hover:ring-slate-300/60 transition-all overflow-hidden ${
        accent
          ? "ring-amber-200/70 bg-gradient-to-br from-white to-amber-50/40"
          : ""
      }`}>
      <div className="flex items-start justify-between mb-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg} ${iconColor}`}>
          {icon}
        </div>
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </p>
      <div className="text-xl font-bold text-slate-900 leading-tight">
        {value}
      </div>
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}
